<template>
  <section
    ref="customerSection"
    class="vf-o-our-customers-section"
    aria-labelledby="our-customers-title"
  >
    <div class="our-customers-section__inner">
      <h2 id="our-customers-title" class="our-customers-section__title">
        {{ title }}
      </h2>
      <p class="our-customers-section__intro">
        See how Hooligans frames look in real life. Tap a look to visit the
        customer story or featured frame.
      </p>

      <CustomerCarousel
        v-if="items.length"
        label="Our customers"
        class="our-customers-carousel"
        :items="items"
      >
        <template #slide="{ slide }">
          <component
            :is="customerCardComponent(slide)"
            v-bind="customerCardBindings(slide)"
            class="our-customers-card"
            :aria-label="customerCardLabel(slide)"
            @click="trackCustomerClick(slide)"
          >
            <video
              v-if="slide.mediaKind === 'video'"
              class="our-customers-card__media"
              muted
              loop
              playsinline
              preload="none"
              data-customer-video
              :aria-label="slide.alt"
              @loadedmetadata="muteVideo"
              @play="muteVideo"
              @volumechange="muteVideo"
            >
              <source
                v-if="slide.mediaType"
                :src="slide.image"
                :type="slide.mediaType"
              />
              <source v-else :src="slide.image" />
              <!-- Decorative, muted clip with no dialogue; empty captions
                   track documents that and satisfies WCAG 1.2.2 tooling. -->
              <track
                kind="captions"
                srclang="en"
                label="No dialogue"
                src="data:text/vtt,WEBVTT%0A%0A"
              />
            </video>
            <MarketingImage
              v-else
              :media="slide.media"
              :fallback="slide.image"
              :alt="slide.alt"
              image-class="our-customers-card__media"
              sizes="(max-width: 767px) 78vw, 320px"
              :width="720"
              :height="960"
              loading="lazy"
            />
            <span
              v-if="slide.author || slide.href"
              class="our-customers-card__author"
            >
              <svg
                v-if="slide.author || isInstagramHref(slide.href)"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle
                  cx="17.5"
                  cy="6.5"
                  r=".8"
                  fill="currentColor"
                  stroke="none"
                />
              </svg>
              <span>{{ slide.author || customerLinkText(slide) }}</span>
            </span>
          </component>
        </template>
      </CustomerCarousel>
      <div
        v-else
        class="our-customers-section__media-placeholder"
        aria-hidden="true"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { RouterLink } from "vue-router";
import MarketingImage from "../../atoms/marketing-image/marketing-image.vue";
import type { MarketingMedia } from "../../../utils/marketingMedia";
import { useBehaviorVisibility } from "../../../composables/useBehaviorVisibility";
import { trackBehaviorEvent } from "../../../utils/behaviorAnalytics";
import CustomerCarousel from "../../molecules/customer-carousel/customer-carousel.vue";
import {
  parseCustomerIdentity,
  safeCustomerHref,
} from "../../../utils/customerSocial";

type CustomerBanner = {
  main_title?: string;
  banner_id?: string;
  title?: string;
  link?: string;
  image?: string;
  media?: MarketingMedia | null;
  status?: string;
};

type CustomerSlide = {
  id: string;
  image: string;
  media: MarketingMedia | null;
  mediaKind: "image" | "video";
  mediaType: string;
  author: string;
  alt: string;
  href: string;
};

const DEFAULT_TITLE = "OUR CUSTOMERS";
const REST_API_URL = (import.meta.env.VITE_REST_API_URL || "").replace(
  /\/+$/,
  ""
);

const title = DEFAULT_TITLE;
const banners = ref<CustomerBanner[]>([]);
const customerSection = ref<HTMLElement | null>(null);
let muteObserver: MutationObserver | null = null;
let videoVisibilityObserver: IntersectionObserver | null = null;
useBehaviorVisibility(customerSection, "home_customers_view");

const getMediaExtension = (url = "") => {
  return url.split("?")[0].split("#")[0].split(".").pop()?.toLowerCase() || "";
};

const getMediaKind = (url = "") => {
  const extension = getMediaExtension(url);

  return ["mp4", "mov", "webm"].includes(extension) ? "video" : "image";
};

const getVideoType = (url = "") => {
  const extension = getMediaExtension(url);

  if (extension === "mp4") {
    return "video/mp4";
  }

  if (extension === "webm") {
    return "video/webm";
  }

  return "";
};

const muteVideo = (event: Event) => {
  const video = event.target as HTMLVideoElement;

  video.muted = true;
  video.defaultMuted = true;
  video.volume = 0;
};

const prepareCustomerVideos = () => {
  customerSection.value
    ?.querySelectorAll<HTMLVideoElement>("video")
    .forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.volume = 0;
      video.setAttribute("muted", "");
      videoVisibilityObserver?.observe(video);
    });
};

const customerCardLabel = (slide: CustomerSlide) => {
  const customer = slide.author || "a Hooligans customer";
  if (!slide.href) return `${customer} wearing Hooligans Eyewear`;
  if (isInternalHref(slide.href)) {
    return `View the Hooligans frame featured on ${customer}`;
  }
  if (isInstagramHref(slide.href)) {
    return `View ${customer} wearing Hooligans Eyewear on Instagram`;
  }
  return `View the customer story for ${customer}`;
};

const isInternalHref = (href = "") =>
  href.startsWith("/") && !href.startsWith("//");
const isInstagramHref = (href = "") =>
  /^https?:\/\/(?:www\.)?instagram\.com\//i.test(href);

const customerCardComponent = (slide: CustomerSlide) => {
  if (!slide.href) return "div";
  return isInternalHref(slide.href) ? RouterLink : "a";
};

const customerCardBindings = (slide: CustomerSlide) => {
  if (!slide.href) return { role: "group" };
  if (isInternalHref(slide.href)) {
    return {
      to: slide.href,
      "data-navigation-kind": "product",
      "data-shared-image": "false",
    };
  }
  return {
    href: slide.href,
    target: "_blank",
    rel: "noopener noreferrer",
  };
};

const customerLinkText = (slide: CustomerSlide) => {
  if (isInternalHref(slide.href)) return "View featured frame";
  if (isInstagramHref(slide.href)) return "View on Instagram";
  return "View customer story";
};

const trackCustomerClick = (slide: CustomerSlide) => {
  if (!slide.href) return;
  const detail = isInternalHref(slide.href)
    ? "internal_product"
    : isInstagramHref(slide.href)
    ? "instagram"
    : "external_story";
  trackBehaviorEvent("home_customer_click", detail);
};

const items = computed<CustomerSlide[]>(() =>
  banners.value
    .filter((banner) => banner.status === "1" && banner.image)
    .map((banner, index) => {
      const href = safeCustomerHref(banner.link);
      const parsedTitle = parseCustomerIdentity(banner.title, href);
      const author = parsedTitle.author;

      return {
        id: banner.banner_id || `customer-${index}`,
        image: banner.image || "",
        media: banner.media || null,
        mediaKind: getMediaKind(banner.image),
        mediaType: getVideoType(banner.image),
        author,
        alt: parsedTitle.alt || author || "Customer wearing Hooligans Eyewear",
        href,
      };
    })
);

const loadCustomers = async () => {
  if (!REST_API_URL) {
    return;
  }

  try {
    const url = `${REST_API_URL}?route=api%2Fbanner&banner=customers`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to load customer banners: ${response.status}`);
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      return;
    }

    banners.value = data;
    await nextTick();
    prepareCustomerVideos();
  } catch (error) {
    console.error("Failed to load customer banners:", error);
  }
};

onMounted(() => {
  videoVisibilityObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target as HTMLVideoElement;
        video.muted = true;
        video.defaultMuted = true;
        video.volume = 0;
        video.setAttribute("muted", "");

        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= 0.45 &&
          !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
          const playPromise = video.play();
          playPromise?.catch(() => {});
        } else {
          video.pause();
        }
      });
    },
    { threshold: [0, 0.45, 0.75] }
  );

  loadCustomers();

  muteObserver = new MutationObserver(() => {
    prepareCustomerVideos();
  });

  if (customerSection.value) {
    muteObserver.observe(customerSection.value, {
      childList: true,
      subtree: true,
    });
  }
});

onBeforeUnmount(() => {
  muteObserver?.disconnect();
  videoVisibilityObserver?.disconnect();
});

watch(items, async () => {
  await nextTick();
  prepareCustomerVideos();
});
</script>
